import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {findParameterWithContext} from './findParameter.js';
import {shuffle} from 'utils/ShuffleArray.js';
import {PARAMS_TO_IGNORE, getTrackParameters} from './getTrackParameters.js';
import {setEffectParameterValues} from 'actions/index.js';

export default superclass => class extends superclass  {
    _parameters = [];
    initialParameterValues = [];
    initialParameterDepths = [];
    depthParameters = [];

    newDepthParameter(parameter) {
        let depthParameter = new AbstractParameter(
            "depth", this, (o,x) => x,
            {
                units: "%",
                min: 0,
                max: 100,
                defaultValue: 100
            });

        if (this.context) {
            depthParameter.setContext(this.context);
        }
        this.registerParameter(depthParameter);
        return depthParameter;
    }

    getDepth(i) {
        return this.depthParameters[i] ?
            this.depthParameters[i].get() : 0;
    }

    setupRampParameters() {
        this.rampTimeParameter = new AbstractParameter(
            "rampTime", this, (o,x) => x,
            {
                documentation: "Time it takes (in ms) to reach the value its adding",
                min: 0,
                max: 10000,
                defaultValue: 80
            });

        this.registerParameter(this.rampTimeParameter);

        this.resetAtZeroParameter = new AbstractParameter(
            "resetAtZero", this, (o,x) => x,
            {
                documentation: "Should the accumulator reset to 0 upon reaching step 1 in the pattern",
                min: 0,
                max: 1,
                defaultValue: false
            });

        this.registerParameter(this.resetAtZeroParameter);

        this.parametersDisabledParameter = new AbstractParameter(
            "parametersDisabled",
            this,
            (o, x) => this.parametersDisabledChanged(x),
            {
                defaultValue: []
            });
        this.registerParameter(this.parametersDisabledParameter);
    }

    get parametersDisabled() {
        return this.parametersDisabledParameter.get(true);
    }

    parametersDisabledChanged(x) {
        let bulk = {};
        for (let i of x) {
            let value = this.initialParameterValues[i];
            if (value !== undefined && this._parameters[i]) {
                let {id} = this._parameters[i].set(
                    value, true, true, undefined,
                    this.rampTime / 1000.0);
                bulk[id] = value;
            }
        }
        this.setEffectParameterValues(bulk);
    }

    getInitialValueForParameter(parameter, i=this._parameters.indexOf(parameter)) {
        return this.initialParameterValues[i];
    }

    setInitialValueForParameter(parameter, value) {
        let indexOf = this._parameters.indexOf(parameter);
        this.initialParameterValues[indexOf] = value;
    }

    shouldSkipParameter(i) {
        return false;
    }

    // sets the parameters to the INITIAL values before playing
    resetValues() {
        let bulk = {};

        for (let i in this._parameters) {
            if (this.shouldSkipParameter(i)) {
                continue;
            }
            let parameter = this._parameters[i];
            if (this.initialParameterValues[i] !== undefined) {
                let initialValue = this.initialParameterValues[i];
                if (initialValue < parameter.min ||
                    initialValue > parameter.max) {
                    // the min/max changed while playing so set to avg
                    // of min/max
                    initialValue = (parameter.min + parameter.max) / 2.0;
                }
                let ret = parameter.set(initialValue, true, true);
                bulk[ret.id] = ret.value;
                if (parameter.depthParameter) {
                    let {depthParameter} = parameter;
                    let initialDepth =this.initialParameterDepths[i];
                    if (initialDepth < depthParameter.min ||
                        initialDepth > depthParameter.max) {
                        // the depth min/max changed while playing so set to avg
                        // of min/max
                        initialDepth = (depthParameter.min + depthParameter.max) / 2.0;
                    }
                    ret = this._parameters[i].depthParameter.set(
                        initialDepth, true, true);
                    bulk[ret.id] = ret.value;
                }else {
                    parameter.depth = this.initialParameterDepths[i];
                }
            } else {
            }
        }
        this.setEffectParameterValues(bulk);
    }

    setEffectParameterValues(bulk) {
        this.track.sequencerEngine.dispatch(
            setEffectParameterValues(bulk));
    }

    stop() {
        this.resetValues();
    }

    unmap(p) {
        let i = this._parameters.indexOf(p);
        this.unmapParameter(i);
    }

    unmapParameter(i) {
        let parameter = this._parameters[i];
        if (!parameter) {
            return;
        }
        parameter.locked = undefined;
        this._parameters.splice(i, 1);
        let depthParameter = this.depthParameters[i];
        this.depthParameters.splice(i, 1);

        let index = this.parameters.indexOf(depthParameter);
        this.parameters.splice(index, 1);
        
        let value = this.initialParameterValues[i];
        this.initialParameterValues.splice(i, 1);
        if (value !== undefined) {
            parameter.set(value, true, false);
        }
        parameter[this.key] = undefined;
        return parameter;
    }

    get key() {
        return "isMapped";
    }
   

    setParameter(parameter, i=this._parameters.length) {
        this._parameters[i] = parameter;
        this.initialParameterValues[i] = parameter.get(true);
        if (!this.depthParameters[i]) {
            this.depthParameters[i] = this.newDepthParameter(parameter);
        }
        parameter[this.key]  = this;
    }

    _clear() {
        this.initialParameterValues = [];

        // upon restarting we need to reset the parameter values
        for (let i in this._parameters) {
            if (this.shouldSkipParameter(i)) {
                continue;
            }
            let parameter = this._parameters[i];
            this.clearParameter(parameter);
        }
    }

    clearParameter(parameter, i) {
        this._clearParameter(parameter, i);
    }

    // sets the initialParameterValues
    _clearParameter(parameter, i=this._parameters.indexOf(parameter)) {
        if (!parameter) {
            return;
        }
        this.initialParameterValues[i] = parameter.get(true);
        if (parameter.depthParameter) {
            let depth = parameter.depthParameter.get(true);
            if (isNaN(depth)) {
                this.initialParameterDepths[i] = 1;
            } else {
                this.initialParameterDepths[i] = depth;
            }
        } else {
            this.initialParameterDepths[i] = parameter.depth;
        }
    }

    get resetAtZero() {
        return this.resetAtZeroParameter.get();
    }

    get rampTime() {
        return this.rampTimeParameter.get();
    }

    onNewStep() {
        this.triggers = [];
    }

    clear() {
        super.clear();
        this._clear();
    }

    // getParameterValueToSet should be implemented in the class using this
    getParameterValueToSet(i) { return 0; }

    setParameterValues(time) {
        let bulk ={};
        for (let i in this._parameters) {
            i = parseInt(i);
            if (this.parametersDisabled.includes(i)) {
                continue;
            }

            let _value =  this.getParameterValueToSet(i); 
            let parameter = this._parameters[i];

            if (isNaN(_value) || parameter.get(true) === _value) {
                // dont set the parameter if it has not changed
                continue;
            }

            let {max, min} = parameter;

            let {id, value} = parameter.set(
                Math.min(max, Math.max(min, _value)),
                true,
                true,
                time,
                this.rampTime / 1000.0);
            bulk[id] = value;
        }

        // update redux with these new values
        this.track.sequencerEngine.parametersToUpdate = {
            ... this.track.sequencerEngine.parametersToUpdate,
            ... bulk
        };
    }

    loadParameters(parameters, initialLoad=false) {
        let newMappings = {};

        // upon initial load, dont use the context to try to find parameters
        // if the ids dont match
        // but upon second load, if we dont find the id we want, we use the context
        // to find a best match
        let sequencerEngine = this.track.sequencerEngine;
        let lostParameters = [];
        for (let parameter of parameters) {
            let id = typeof parameter === "object" ? parameter.id : parameter;
            // need to find the parameter within the tracks
            let foundParameter = undefined;
            let {subSequence} = this.track;
            if (parameter.name === "BPM") {
                foundParameter = sequencerEngine.bpmParameter;
            }
            for (let t of subSequence.tracks) {
                let _foundParameter = t.getAllParameters().find(p => id === p.id);
                if (_foundParameter) {
                    foundParameter = _foundParameter;
                    break;
                }
            }

            if (!foundParameter) {
                // then we need to look at lfo parameters and envelope parameters
                for (let lfo of subSequence.lfos) {
                    lfo = lfo.lfo;
                    let _foundParameter = lfo.parameters.find(p => id === p.id);
                    if (_foundParameter) {
                        foundParameter = _foundParameter;
                        break;
                    }
                }
            }
            if (!foundParameter) {
                for (let subSequence of this.track.sequencerEngine.subSequences) {
                    let effects = [... subSequence.midiEffects.effects, subSequence.playhead];
                    let _parameters = effects.flatMap(x => x.getParameters());
                    foundParameter = _parameters.find(p => id === p.id);
                    if (foundParameter) {
                        break;
                    }
                }
            }
            if (!foundParameter) {
                let _parameters = sequencerEngine.sends.sends.flatMap(x => x.effects)
                    .flatMap(x => x.getParameters());
                foundParameter = _parameters.find(p => id === p.id);
            }

            if (!foundParameter) {
                for (let env of subSequence.envelopes) {
                    let _foundParameter = env.parameters.find(p => id === p.id);
                    if (_foundParameter) {
                        foundParameter = _foundParameter;
                        break;
                    }
                }
            }


            if (!foundParameter && !initialLoad && typeof parameter === "object") {
                foundParameter = findParameterWithContext(
                    parameter, this.track, this.track.sequencerEngine);
                // need to map the id to this found parameter
                if (foundParameter) {
                    newMappings[id] = foundParameter;
                    let {x,y} = parameter.coordinates;
                    // foundParameter.setCoordinates(x, y);
                }
            } 

            if (foundParameter) {
                this.setParameter(foundParameter);
            } else {
                lostParameters.push(parameter);
            }
        }

        this.sortParameters();
        return {lostParameters, newMappings};
    }

    sortParameters() {
        if (!this.parametersJson) {
            return;
        }
        let parameters = this.parametersJson.map(
            ({id}, i) => this._parameters.find(
                p => p && p.id === id))
            .filter(p => p);
        this._parameters = parameters;
    }

    loadDepthJson(json, isCopy) {
        if (json.depthParameters) {
            let i = 0;
            for (let _depthParameter of json.depthParameters) {
                let depthParameter = this.newDepthParameter();
                depthParameter.fromJson(_depthParameter, this.track.sequencerEngine, isCopy);
                this.depthParameters[i++] = depthParameter;
            }
        }
    }

    getMappableJson() {
        return {
            parameters: this._parameters.map(x => ({
                id: x.id,
                context: x.context && x.context.getJson(),
                name: x.name,
                coordinates: x.getCoordinates()
            })),
            depthParameters: this.depthParameters.map(
                x => x.getJson())
        };
    }

    shouldOutputSkip(time, stepNumber) {
        if (stepNumber === 0 && this.resetAtZero) {
            this.stop();
            this.clear();
            return true;
        }

        if (this.triggers.length === 0) {
            return true;
        }

        return false;
    }

    getJson() {
        let json = {
            ... super.getJson(),
            ... this.getMappableJson(),
        };
        return json;
    }

    fromJson(json, isCopy) {
        delete json.depth;
        this.parametersJson = json.parameters;
        let bulk = super.fromJson(json, isCopy);


        // keep track of any parameters that we've lost so we can search for them
        // at the end of load

        this.lostParameters = this.loadParameters(json.parameters, true).lostParameters;
        this.loadDepthJson(json, isCopy);

        return bulk;
    }

    // get a bunch of parameters
    randomlyMapParameters(device, numParameters=200, parameters=getTrackParameters(device, this)) {
        /*
        let lfos = this.track.sequencerEngine.lfos.map(x => x.lfo);
        for (let lfo of lfos) {
            parameters = [
                ... lfo.parameters.filter(x => !PARAMS_TO_IGNORE.includes(x.name)),
                ... parameters];
        }
        */

        // if any of these are formants, then make sure to only
        // add them if they are enabled
        let _parameters = [];
        for (let p of parameters) {
            if (p.context && p.context.formant) {
                let formant = p.context.formant;
                if (!formant.enabledParameter.get()) {
                    continue;
                } 
            }
            _parameters.push(p);
        }

        shuffle(_parameters);
        _parameters = Array.from(new Set(_parameters));
        _parameters = _parameters.slice(0, numParameters);

        // set the parameters
        _parameters.forEach(
            p => this.setParameter(p));

        return _parameters;
    }

    dispose() {
        // first get rid of all mappings
        while (this._parameters.length > 0) {
            this.unmapParameter(0);
        }

        super.dispose();
    }

    replaceParameter(parameter) {
        for (let i in this._parameters) {
            if (this._parameters[i].id === parameter.id) {
                this._parameters[i] = parameter;
                return;
            }
        }
    }
}

